import React from 'react';
import { debounce } from '../../utils/debounce';
/**
 * Find an option by its id.
 * Assumes the list is never nested more than one level deep.
 * Only matches leaf nodes (non-group options).
 */
const findOptionById = (options, targetId) => {
  // Create a flattened list of all leaf nodes (removing groups)
  const flattenedOptions = options.flatMap(option => option.subOptions ? option.subOptions : option);
  return flattenedOptions.find(option => option.id === targetId) || null;
};

/**
 * Program designations search select hook
 */
export const useProgramDesignationsSearchSelect = ({
  initialOptions,
  selectedOption,
  onSearch
}) => {
  const [options, setOptions] = React.useState(initialOptions);
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  /**
   * Per the design, the program designations search should only be visible when there
   * are multiple initial options.
   */
  const hasMoreThanOneProgramDesignation = React.useMemo(() => {
    if (initialOptions.length > 1) return true;

    // If there is only one option, check if it is a group with multiple sub-options
    if (initialOptions.length && initialOptions[0].subOptions && initialOptions[0].subOptions.length > 1) {
      return true;
    }
    return false;
  }, [initialOptions]);

  // Support changing the options and selected value after initial render
  React.useEffect(() => {
    // Don't do anything if there are no options (prevents infinite loops in Donation too)
    if (!initialOptions || initialOptions.length === 0) return;
    const newSelectedOption = selectedOption ? findOptionById(initialOptions, String(selectedOption.id)) : null;
    setValue(newSelectedOption);
    setInputValue(newSelectedOption?.label || '');
    setOptions(initialOptions);
  }, [initialOptions, selectedOption]);
  const debouncedSearch = React.useMemo(() => debounce(async searchString => {
    if (!searchString) {
      return;
    }
    try {
      setIsLoading(true);
      const results = await onSearch(searchString);
      setOptions(results);
    } catch (e) {
      // TODO: handle error, or is setting an empty array okay?
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, 300), [onSearch]);
  React.useEffect(() => {
    // Don't do anything if there are no options (prevents infinite loops in Donation too)
    if (inputValue === value?.label || !initialOptions || initialOptions.length === 0) {
      return () => {};
    }
    if (!inputValue) {
      // If the inputValue is empty, reset to the initial options. No need to fetch them.
      setOptions(initialOptions);
    } else {
      debouncedSearch(inputValue);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, value?.label, debouncedSearch, initialOptions, selectedOption]);
  return {
    options,
    value,
    setValue,
    inputValue,
    setInputValue,
    isLoading,
    hasMoreThanOneProgramDesignation
  };
};